import { flip, offset, shift, useFloating } from "@floating-ui/react";
import { Combobox as HeadlessCombobox } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { XMarkIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { useRef, useState } from "react";
import { Input } from "components/elements";
import { cn } from "lib/cn";

export const Combobox = <T extends string | number>({
    value,
    onChange,
    displayValue,
    options,
    className,
    inputClassName,
    placeholder,
    withClear,
}: {
    value: T | null | undefined;
    onChange: (value: T | null | undefined) => void;
    displayValue: (value: T | null | undefined) => string;
    options: (query: string) => { value: T; display: string }[] | null;
    className?: string;
    inputClassName?: string;
    placeholder?: string;
    withClear?: boolean;
}) => {
    const { elements, x, y, strategy, refs } = useFloating({
        middleware: [
            offset(4), // Add some space between the input and the dropdown
            flip(), // Flip the dropdown if it overflows the viewport
            shift({ padding: 5 }), // Shift the dropdown to stay within the viewport
        ],
    });
    const [query, setQuery] = useState("");
    const optionsRef = useRef<HTMLUListElement>(null);
    const filteredOptions = options(query);

    // Handle click events to prevent closing when scrolling
    const handleOptionsMouseDown = (e: React.MouseEvent) => {
        // Check if the click is on the scrollbar area
        const rect = optionsRef.current?.getBoundingClientRect();
        if (rect && optionsRef.current) {
            const scrollbarWidth = optionsRef.current.offsetWidth - optionsRef.current.clientWidth;
            const clickX = e.clientX - rect.left;
            // If clicking in the scrollbar area, prevent default
            if (clickX > rect.width - scrollbarWidth - 10) {
                e.preventDefault();
                e.stopPropagation();
            }
        }
    };

    return (
        <HeadlessCombobox value={value} onChange={onChange} nullable>
            <div className={clsx("relative min-w-max flex-row", className)} ref={refs.setReference}>
                {/* Make the entire container clickable with cursor-pointer */}
                <HeadlessCombobox.Button as="div" className="w-full cursor-pointer">
                    <div className="relative">
                        <HeadlessCombobox.Input
                            onChange={(e) => setQuery(e.target.value)}
                            displayValue={displayValue}
                            className={cn(
                                inputClassName,
                                withClear
                                    ? "w-full pr-14 focus:ring-0"
                                    : "w-full pr-9 focus:ring-0",
                                "cursor-pointer", // Add cursor-pointer to input as well
                            )}
                            placeholder={placeholder}
                            autoComplete="off"
                            style={{ backgroundColor: "#f7f7f7" }}
                            as={Input}
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center px-2">
                            {/* This button is now just for visual, but clicking it will be handled by the parent button */}
                            <ChevronDownIcon className="h-6 w-6 p-1 text-slate-400" />
                            {withClear && (query || value) && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent the button click from opening the dropdown
                                        setQuery("");
                                        onChange(null);
                                    }}
                                    className="cursor-pointer" // Ensure clear button has cursor-pointer
                                >
                                    <XMarkIcon className="h-6 w-6 p-1 text-slate-400" />
                                </button>
                            )}
                        </div>
                    </div>
                </HeadlessCombobox.Button>
            </div>
            <HeadlessCombobox.Options
                ref={(node) => {
                    refs.setFloating(node);
                    optionsRef.current = node;
                }}
                className="z-99999 max-h-60 w-full overflow-y-auto rounded-lg border border-slate-300 bg-[#def1fa] pl-0 shadow-lg focus:outline-hidden"
                style={{
                    position: "fixed",
                    top: y ?? 0,
                    left: x ?? 0,
                    width: elements.reference?.getBoundingClientRect().width ?? 0,
                    scrollbarWidth: "thin",
                    scrollbarColor: "#cbd5e1 transparent",
                }}
                onMouseDown={handleOptionsMouseDown}
            >
                {!filteredOptions && (
                    <div className="px-2 py-2 text-sm text-gray-500">Loading...</div>
                )}
                {filteredOptions?.length === 0 && (
                    <div className="px-2 py-2 text-sm text-gray-500">No options found</div>
                )}
                {filteredOptions?.map((option) => (
                    <HeadlessCombobox.Option
                        key={option.value}
                        value={option.value}
                        className={({ active, selected }) =>
                            clsx(
                                "w-full cursor-pointer px-2 py-2 text-sm select-none",
                                active && "bg-sky-200",
                                selected && "bg-sky-100 font-medium",
                                !active && !selected && "hover:bg-sky-50",
                            )
                        }
                    >
                        {option.display}
                    </HeadlessCombobox.Option>
                ))}
            </HeadlessCombobox.Options>
        </HeadlessCombobox>
    );
};
